export const PERIOD_LENGTH_CATEGORY = 'Period length';
export const PERIOD_TIMEFRAME_CATEGORY = 'Period timeframe';

export const PERIOD_CATEGORY_NAMES = new Set([
  PERIOD_LENGTH_CATEGORY,
  PERIOD_TIMEFRAME_CATEGORY,
]);

export const PERIOD_TIMEFRAME_FACTORS = Object.freeze({
  Weeks: 1,
  Months: 4.33,
  Years: 52,
});

export function isPeriodCategory(category) {
  return PERIOD_CATEGORY_NAMES.has(category?.name);
}

export function isPeriodTag(tag, categories) {
  return isPeriodCategory(categories?.map?.get(tag?.category_uuid));
}

export function hasPeriodCategories(categories) {
  return (
    categories?.list?.some(({ name }) => name === PERIOD_LENGTH_CATEGORY) &&
    categories?.list?.some(({ name }) => name === PERIOD_TIMEFRAME_CATEGORY)
  );
}

export function getActivityFrequencyConfig(categories, tags) {
  const lengthCategory = categories?.list?.find(
    ({ name }) => name === PERIOD_LENGTH_CATEGORY
  );
  const timeframeCategory = categories?.list?.find(
    ({ name }) => name === PERIOD_TIMEFRAME_CATEGORY
  );

  if (!lengthCategory || !timeframeCategory) {
    return null;
  }

  const lengthCategoryTags = tags.list.filter(
    ({ category_uuid }) => category_uuid === lengthCategory.uuid
  );
  const timeframeCategoryTags = tags.list.filter(
    ({ category_uuid }) => category_uuid === timeframeCategory.uuid
  );
  const lengthTags = lengthCategoryTags
    .filter(({ name }) => Number.isInteger(Number(name)) && Number(name) > 0)
    .sort((a, b) => Number(a.name) - Number(b.name));
  const timeframeTags = timeframeCategoryTags
    .filter(({ name }) => Object.hasOwn(PERIOD_TIMEFRAME_FACTORS, name))
    .sort(
      (a, b) =>
        Object.keys(PERIOD_TIMEFRAME_FACTORS).indexOf(a.name) -
        Object.keys(PERIOD_TIMEFRAME_FACTORS).indexOf(b.name)
    );

  return {
    lengthCategory,
    lengthCategoryTags,
    lengthTags,
    timeframeCategory,
    timeframeCategoryTags,
    timeframeTags,
    defaultLengthTag: lengthTags.find(({ name }) => name === '1') ?? null,
    defaultTimeframeTag:
      timeframeTags.find(({ name }) => name === 'Weeks') ?? null,
  };
}

export function resolveActivityFrequency(tags, config) {
  const selected = new Set(tags ?? []);
  const lengthTag =
    config.lengthTags.find(({ uuid }) => selected.has(uuid)) ??
    config.defaultLengthTag;
  const timeframeTag =
    config.timeframeTags.find(({ uuid }) => selected.has(uuid)) ??
    config.defaultTimeframeTag;

  return {
    length: Number(lengthTag?.name) || 1,
    lengthTag,
    timeframe: timeframeTag?.name ?? 'Weeks',
    timeframeTag,
  };
}

export function calculateWeeklyHours(instanceHours, length, timeframe) {
  const factor = PERIOD_TIMEFRAME_FACTORS[timeframe];
  const hours = Number(instanceHours);
  const periodLength = Number(length);

  if (!Number.isFinite(hours) || !Number.isFinite(periodLength) || !factor) {
    return 0;
  }

  return Math.round((hours / (periodLength * factor)) * 100) / 100;
}

export function calculateInstanceHours(weeklyHours, length, timeframe) {
  const factor = PERIOD_TIMEFRAME_FACTORS[timeframe];
  const hours = Number(weeklyHours);
  const periodLength = Number(length);

  if (!Number.isFinite(hours) || !Number.isFinite(periodLength) || !factor) {
    return 0;
  }

  return Math.min(
    500,
    Math.max(0, Math.round(hours * periodLength * factor * 100) / 100)
  );
}

export function replacePeriodTag(tags, categoryTags, selectedTag) {
  const categoryTagIds = new Set(categoryTags.map(({ uuid }) => uuid));
  const updated = (tags ?? []).filter((uuid) => !categoryTagIds.has(uuid));

  if (selectedTag) updated.push(selectedTag.uuid);

  return updated;
}
